import React, { useContext, useEffect, useState, useMemo } from "react";
import { useAuthContext } from "../../contexts/AuthContext";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";

import Sidebar from "../UI/Sidebar";
import Loader from "../UI/Loader";
import ErrorMessage from "../UI/ErrorMessage";
import PostItTimelineView from "./PostItTimelineView";
import { sfQuery } from "../API";

const TasksView = () => {
  const [tasks, setTasks] = useState([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPriority, setSelectedPriority] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedUser, setSelectedUser] = useState(""); // Start empty, will be set to current user
  const [availableUsers, setAvailableUsers] = useState([]);

  const context = useAuthContext();
  const { sidebar } = useParams();

  // Set default user to current logged-in user when context is ready
  useEffect(() => {
    if (context?.ready && context?.userInfo?.userFullName && !selectedUser) {
      setSelectedUser(context.userInfo.userFullName);
    }
  }, [context?.ready, context?.userInfo?.userFullName, selectedUser]);

  // Priority options for tasks (improved categorization)
  const priorityOptions = ["All", "Urgent", "Medium", "Normal"];

  // Status options for tasks
  const statusOptions = [
    "All",
    "Not Started",
    "In Progress",
    "Completed",
    "Waiting on someone else",
    "Deferred",
  ];

  // Priority mapping for cleaner categorization
  const getPriorityCategory = (priority) => {
    if (!priority) return "Normal";

    const priorityLower = priority.toLowerCase();
    if (
      priorityLower.includes("high") ||
      priorityLower.includes("urgent") ||
      priorityLower.includes("p0") ||
      priorityLower.includes("p1") ||
      priorityLower.includes("p2") ||
      priorityLower.includes("time-sensitive") ||
      priorityLower.includes("today")
    ) {
      return "Urgent";
    }
    if (priorityLower.includes("normal")) {
      return "Medium";
    }
    return "Normal"; // Low priority
  };

  // Processed data for PostItTimelineView
  const processedData = useMemo(() => {
    if (!context?.ready) return [];

    // Filter out tasks that were completed before our timeline window
    const timelineStartDate = dayjs().subtract(1, "week").startOf("day");

    const relevantTasks = tasks.filter((task) => {
      // Always include open tasks
      if (task.Status !== "Completed") {
        return true;
      }

      // For completed tasks, only include if they were completed during or after our timeline window
      const completedDateStr = task.CompletedDateTime || task.LastModifiedDate;
      if (!completedDateStr) {
        // If no completion date, include it (might be a data issue)
        return true;
      }

      // Convert CompletedDateTime to date (remove time component)
      const completedDate = dayjs(completedDateStr).startOf("day");
      if (!completedDate.isValid()) {
        // If invalid date, include it (might be a data issue)
        return true;
      }

      return (
        completedDate.isAfter(timelineStartDate) ||
        completedDate.isSame(timelineStartDate, "day")
      );
    });

    console.log(
      `Timeline Filter: ${tasks.length} total tasks -> ${
        relevantTasks.length
      } relevant tasks (filtered out ${
        tasks.length - relevantTasks.length
      } old completed tasks)`
    );

    return relevantTasks.map((task) => {
      const startDate = task.ActivityDate || task.CreatedDate;

      // For completed tasks, use the actual completion date or calculate based on status
      let endDate;
      if (task.Status === "Completed") {
        // If completed, use the actual completion date or calculate a reasonable end date
        endDate =
          (task.CompletedDateTime
            ? dayjs(task.CompletedDateTime).format("YYYY-MM-DD")
            : null) ||
          (task.LastModifiedDate
            ? dayjs(task.LastModifiedDate).format("YYYY-MM-DD")
            : null) ||
          dayjs(startDate).add(3, "day").format("YYYY-MM-DD");
      } else {
        // For open tasks, use activity date or add reasonable duration
        endDate =
          task.ActivityDate ||
          dayjs(startDate).add(2, "day").format("YYYY-MM-DD");
      }

      return {
        id: task.Id,
        createdDate: startDate,
        expectedDate: endDate,
        priority: getPriorityCategory(task.Priority),
        originalPriority: task.Priority || "Normal",
        status: task.Status,
        description: task.Description || task.Subject || `Task ${task.Id}`,
        assignedTo: task.Owner?.Name || "Unassigned",
        type: "task",
      };
    });
  }, [tasks, context?.ready]);

  // Get available users for filter - fetch from Salesforce
  useEffect(() => {
    if (!context?.ready) return;

    const fetchUsers = async () => {
      try {
        const users = await sfQuery("User", "Name", "IsActive = true", context);
        const userNames = users.map((user) => user.Name).sort();
        setAvailableUsers(userNames);
      } catch (err) {
        console.error("Error fetching users:", err);
        // Fallback to current user only
        if (context?.userInfo?.userFullName) {
          setAvailableUsers([context.userInfo.userFullName]);
        }
      }
    };

    fetchUsers();
  }, [context]);

  // Filtered data based on search and filters
  const filteredData = useMemo(() => {
    if (!context?.ready) return [];

    return processedData.filter((task) => {
      const matchesSearch =
        searchQuery === "" ||
        task.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.assignedTo.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesPriority =
        selectedPriority === "All" || task.priority === selectedPriority;
      const matchesStatus =
        selectedStatus === "All" || task.status === selectedStatus;
      // User filtering is handled by the query itself, so all returned tasks match the selected user

      return matchesSearch && matchesPriority && matchesStatus;
    });
  }, [
    processedData,
    searchQuery,
    selectedPriority,
    selectedStatus,
    context?.ready,
  ]);

  // Statistics (calculated from filtered data in current view only)
  const stats = useMemo(() => {
    // Get the timeline bounds to match GanttView filtering
    const timelineStart = dayjs().startOf("month").startOf("week");
    const timelineEnd = dayjs().add(1, "month").endOf("month").endOf("week");

    // Filter data to match what's shown in GanttView
    const visibleData = filteredData.filter((item) => {
      const itemStart = dayjs(item.createdDate);
      const itemEnd = dayjs(item.expectedDate);
      const itemCompleted = item.status === "Completed";

      // Include all non-completed items, and completed items only if they fall within timeframe
      if (itemCompleted) {
        return (
          itemStart.isBefore(timelineEnd) && itemEnd.isAfter(timelineStart)
        );
      }
      return true;
    });

    const total = visibleData.length;
    const urgent = visibleData.filter((t) => t.priority === "Urgent").length;
    const completed = visibleData.filter(
      (t) => t.status === "Completed"
    ).length;
    const overdue = visibleData.filter((t) => {
      const expectedDate = dayjs(t.expectedDate);
      const isOverdue = dayjs().isAfter(expectedDate);
      const isNotCompleted = t.status !== "Completed";
      return isOverdue && isNotCompleted;
    }).length;

    return { total, urgent, completed, overdue };
  }, [filteredData]);

  // Fetch tasks data for specific user
  useEffect(() => {
    if (!context?.ready || !selectedUser) return;

    setLoading(true);
    const fetchTasks = async () => {
      try {
        console.log("Fetching tasks for user:", selectedUser);
        const tasksData = await sfQuery(
          "Task",
          "Id, CreatedDate, ActivityDate, Subject, Description, Status, Priority, Owner.Name, CompletedDateTime, LastModifiedDate",
          `Owner.Name = '${selectedUser}' AND ActivityDate >= LAST_N_DAYS:30 LIMIT 1000`,
          context
        );
        console.log(
          "Fetched tasks:",
          tasksData.length,
          "tasks for",
          selectedUser
        );
        setTasks(tasksData);
        setError(false);
      } catch (err) {
        console.error("Error fetching tasks:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, [context, selectedUser]);

  // Loading and error states
  if (!context?.ready) return <Loader />;
  if (loading) return <Loader />;
  if (error) return <ErrorMessage />;

  // Show user selection prompt if no user is selected
  if (!selectedUser) {
    return (
      <>
        {!sidebar && <Sidebar />}
        <div className={sidebar ? "contentWOS" : "content"}>
          <div style={{ padding: "20px", textAlign: "center" }}>
            <h1
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontSize: "2.5rem",
                fontWeight: "bold",
                marginBottom: "30px",
              }}
            >
              📋 Tasks Management
            </h1>
            <div
              style={{
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px)",
                borderRadius: "20px",
                padding: "40px",
                maxWidth: "500px",
                margin: "0 auto",
                boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
              }}
            >
              <h3 style={{ marginBottom: "20px", color: "#2d3748" }}>
                Select a User to View Tasks
              </h3>
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                style={{
                  padding: "12px 40px 12px 16px",
                  border: "2px solid #e0e6ed",
                  borderRadius: "10px",
                  fontSize: "16px",
                  background: "white",
                  minWidth: "250px",
                  marginBottom: "20px",
                  backgroundImage:
                    'url(\'data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4 5"><path fill="%23666" d="M2 0L0 2h4zm0 5L0 3h4z"/></svg>\')',
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 12px center",
                  backgroundSize: "12px",
                  appearance: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                }}
              >
                <option value="">Choose a user...</option>
                {availableUsers.map((user) => (
                  <option key={user} value={user}>
                    👤 {user}
                  </option>
                ))}
              </select>
              {context?.userInfo?.userFullName && (
                <div>
                  <button
                    onClick={() =>
                      setSelectedUser(context.userInfo.userFullName)
                    }
                    style={{
                      padding: "12px 24px",
                      border: "none",
                      borderRadius: "10px",
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "white",
                      cursor: "pointer",
                      fontSize: "16px",
                      fontWeight: "bold",
                      transition: "all 0.3s ease",
                      boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
                    }}
                  >
                    📋 View My Tasks
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {!sidebar && <Sidebar />}
      <div className={sidebar ? "contentWOS" : "content"}>
        <div style={{ padding: "20px" }}>
          <h1
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontSize: "2.5rem",
              fontWeight: "bold",
              marginBottom: "30px",
            }}
          >
            📋 Tasks Management
          </h1>

          {/* Statistics Cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "20px",
              marginBottom: "30px",
            }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                padding: "20px",
                borderRadius: "15px",
                textAlign: "center",
                boxShadow: "0 8px 32px rgba(102, 126, 234, 0.3)",
                backdropFilter: "blur(10px)",
              }}
            >
              <h2
                style={{
                  margin: "0 0 5px 0",
                  fontSize: "2rem",
                  color: "white",
                }}
              >
                {stats.total}
              </h2>
              <p style={{ margin: 0, opacity: 0.9, color: "white" }}>
                Total Tasks
              </p>
            </div>
            <div
              style={{
                background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)",
                color: "white",
                padding: "20px",
                borderRadius: "15px",
                textAlign: "center",
                boxShadow: "0 8px 32px rgba(255, 107, 107, 0.3)",
                backdropFilter: "blur(10px)",
              }}
            >
              <h2
                style={{
                  margin: "0 0 5px 0",
                  fontSize: "2rem",
                  color: "white",
                }}
              >
                {stats.urgent}
              </h2>
              <p style={{ margin: 0, opacity: 0.9, color: "white" }}>
                🔥 Urgent
              </p>
            </div>
            <div
              style={{
                background: "linear-gradient(135deg, #00b894 0%, #00a085 100%)",
                color: "white",
                padding: "20px",
                borderRadius: "15px",
                textAlign: "center",
                boxShadow: "0 8px 32px rgba(0, 184, 148, 0.3)",
                backdropFilter: "blur(10px)",
              }}
            >
              <h2
                style={{
                  margin: "0 0 5px 0",
                  fontSize: "2rem",
                  color: "white",
                }}
              >
                {stats.completed}
              </h2>
              <p style={{ margin: 0, opacity: 0.9, color: "white" }}>
                ✅ Completed
              </p>
            </div>
            <div
              style={{
                background: "linear-gradient(135deg, #e17055 0%, #d63031 100%)",
                color: "white",
                padding: "20px",
                borderRadius: "15px",
                textAlign: "center",
                boxShadow: "0 8px 32px rgba(225, 112, 85, 0.3)",
                backdropFilter: "blur(10px)",
              }}
            >
              <h2
                style={{
                  margin: "0 0 5px 0",
                  fontSize: "2rem",
                  color: "white",
                }}
              >
                {stats.overdue}
              </h2>
              <p style={{ margin: 0, opacity: 0.9, color: "white" }}>
                ⚠️ Overdue
              </p>
            </div>
          </div>

          {/* Filters */}
          <div
            style={{
              display: "flex",
              gap: "15px",
              marginBottom: "30px",
              flexWrap: "wrap",
              alignItems: "center",
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              padding: "20px",
              borderRadius: "15px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            }}
          >
            <input
              type="text"
              placeholder="🔍 Search tasks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                padding: "12px 16px",
                border: "2px solid #e0e6ed",
                borderRadius: "10px",
                minWidth: "250px",
                fontSize: "14px",
                background: "white",
                transition: "all 0.3s ease",
                outline: "none",
              }}
            />

            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              style={{
                padding: "12px 40px 12px 16px",
                border: "2px solid #e0e6ed",
                borderRadius: "10px",
                fontSize: "14px",
                background: "white",
                minWidth: "200px",
                backgroundImage:
                  'url(\'data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4 5"><path fill="%23666" d="M2 0L0 2h4zm0 5L0 3h4z"/></svg>\')',
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
                backgroundSize: "12px",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
              }}
            >
              <option value="">Select User...</option>
              {availableUsers.map((user) => (
                <option key={user} value={user}>
                  👤 {user}
                </option>
              ))}
            </select>

            {context?.userInfo?.userFullName &&
              selectedUser !== context.userInfo.userFullName && (
                <button
                  onClick={() => setSelectedUser(context.userInfo.userFullName)}
                  style={{
                    padding: "12px 20px",
                    border: "none",
                    borderRadius: "10px",
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "bold",
                    transition: "all 0.3s ease",
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
                  }}
                  title="Show only my tasks"
                >
                  📋 My Tasks
                </button>
              )}

            <select
              value={selectedPriority}
              onChange={(e) => setSelectedPriority(e.target.value)}
              style={{
                padding: "12px 40px 12px 16px",
                border: "2px solid #e0e6ed",
                borderRadius: "10px",
                fontSize: "14px",
                background: "white",
                minWidth: "130px",
                backgroundImage:
                  'url(\'data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4 5"><path fill="%23666" d="M2 0L0 2h4zm0 5L0 3h4z"/></svg>\')',
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
                backgroundSize: "12px",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
              }}
            >
              {priorityOptions.map((priority) => (
                <option key={priority} value={priority}>
                  🎯 {priority}
                </option>
              ))}
            </select>

            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              style={{
                padding: "12px 40px 12px 16px",
                border: "2px solid #e0e6ed",
                borderRadius: "10px",
                fontSize: "14px",
                background: "white",
                minWidth: "130px",
                backgroundImage:
                  'url(\'data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4 5"><path fill="%23666" d="M2 0L0 2h4zm0 5L0 3h4z"/></svg>\')',
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
                backgroundSize: "12px",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
              }}
            >
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  📊 {status}
                </option>
              ))}
            </select>
          </div>

          {/* Gantt Chart */}
          <div
            style={{
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              borderRadius: "20px",
              padding: "30px",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
              marginBottom: "30px",
            }}
          >
            <h2
              style={{
                fontSize: "1.8rem",
                fontWeight: "bold",
                marginBottom: "20px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              📋 Tasks Post-It Timeline (4 Week View)
            </h2>
            <PostItTimelineView
              data={filteredData}
              type="tasks"
              selectedUser={selectedUser}
              enableStickyHeader={true}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default TasksView;
